<script setup lang="ts">
import { computed } from 'vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  faHeadset,
  faCheck,
  faChevronRight,
  faCirclePause,
} from '@fortawesome/free-solid-svg-icons'
import type { ChatListItem } from '@/components/chat/interfaces/graphql.interfaces.ts'
import { ChatEvent } from '@/enums.ts'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { DAYS_IN_WEEK } from '@/config/config.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import { useUserStore } from '@/stores/user.store.ts'
import { storeToRefs } from 'pinia'
const { t } = useTranslations()

dayjs.extend(isToday)
dayjs.extend(customParseFormat)

const currentDay = dayjs()

const {
  chatIndex,
  isAiControlIntercepted,
  contactEmails,
  contactPhones,
  lastMessage,
  isTyping,
  isCustomerOnline,
} = defineProps<ChatListItem>()
const userStore = useUserStore()
const { hasPresentationModeEnabled } = storeToRefs(userStore)

const lastMessageEvent = computed<string>(() => {
  let lastMessageEvent = ''

  switch (lastMessage.event) {
    case ChatEvent.Customer:
      lastMessageEvent = 'customer'
      break
    case ChatEvent.AI:
      lastMessageEvent = 'AI'
      break
    case ChatEvent.CustomerSupport:
      lastMessageEvent = 'customer Support'
      break
    case ChatEvent.ControlHasBeenInterceptedByCustomerSupport:
      lastMessageEvent = 'Control Intercepted'
      break
    default:
      lastMessageEvent = 'Unknown check manually'
      break
  }

  return lastMessageEvent
})

function toHumanReadableTimeOrDateIfSomeTimeGone(time: string): string {
  const instance = dayjs(time, 'YYYY-MM-DDTHH:mm:ss.000ZZ')

  if (instance.isToday()) {
    return instance.format('HH:mm')
  } else if (currentDay.diff(instance, 'day') < DAYS_IN_WEEK) {
    return instance.format('dddd')
  }

  return instance.format('DD.MM.YY')
}
</script>

<template>
  <div
    class="focus:bg-violet flex items-center h-20 rounded-xl px-2 mx-2 max-w-md shadow-xs border"
    :class="{
      'bg-green-50': isCustomerOnline,
      'bg-fuchsia-300': !isCustomerOnline && isAiControlIntercepted,
      'bg-yellow-100': !isCustomerOnline && !isAiControlIntercepted,
    }"
  >
    <div class="size-10 flex items-center justify-center rounded-full mr-3">
      <font-awesome-icon
        v-if="isAiControlIntercepted"
        :icon="faHeadset"
        class="rounded-full bg-fuchsia-custom text-white size-5 p-2"
      />
      <font-awesome-icon
        v-else-if="isCustomerOnline"
        :icon="faCheck"
        class="rounded-full bg-green-600 text-white size-5 p-2"
      />
      <!-- TODO: Repalce with isActive -->
      <font-awesome-icon
        v-if="!isAiControlIntercepted && !isCustomerOnline"
        :icon="faCirclePause"
        class="rounded-full bg-yellow-custom text-white size-5 p-2"
      />
    </div>

    <!-- Text Content -->
    <div class="w-full flex flex-row">
      <div class="flex-1 flex flex-col">
        <div class="flex flex-row">
          <div class="flex justify-start text-md max-md:text-xs font-semibold text-gray-800">
            {{ t('chats.page.chat') }} #{{ chatIndex }}
          </div>
          <div class="ml-2 text-gray-600">
            <font-awesome-icon v-if="contactEmails" :icon="['fas', 'envelope']" class="mx-1" />
            <font-awesome-icon v-if="contactPhones" :icon="['fas', 'phone']" class="mx-1" />
          </div>
        </div>
        <div v-if="!isTyping" class="text-xs h-12 text-gray-600">
          <span class="text-italic line-clamp-3 max-md:text-xs max-md:line-clamp-2">
            {{ lastMessageEvent }}: <span :class="{ 'blurry-text': hasPresentationModeEnabled }">{{ lastMessage.content }}</span>
          </span>
        </div>
        <div v-else class="flex h-12 items-center justify-center space-x-2 pt-3">
          <div
            class="w-2 h-2 bg-gray-500 rounded-full animate-bounce-inner [animation-delay:-0.3s]"
          ></div>
          <div
            class="w-2 h-2 bg-gray-500 rounded-full animate-bounce-inner [animation-delay:-0.15s]"
          ></div>
          <div class="w-2 h-2 bg-gray-500 rounded-full animate-bounce-inner"></div>
        </div>
      </div>

      <div class="static -top-3 right-0 text-blue-500 text-xs">
        {{ toHumanReadableTimeOrDateIfSomeTimeGone(lastMessage.createdAt) }}
      </div>

      <div class="flex items-center">
        <font-awesome-icon :icon="faChevronRight" class="text-gray-400" />
      </div>
    </div>
  </div>
</template>
